import { type ImageLoaderProps } from 'next/image';
import { clsx, type ClassValue } from 'clsx';
import { env } from 'env.mjs';
import { GraphQLError } from 'graphql';
import Cookies from 'js-cookie';
import { twMerge } from 'tailwind-merge';

import { Theme } from '@/components/common/Providers/GraphqlProvider/generated';

import {
  MAIN_GENERAL_URLS,
  PageBlockConst,
  TOKEN_ADMIN,
  TOKEN_CLIENT,
  type PageBlockConstType,
} from './constants';
import { showErrorToast } from './toast/toastHelpers';

const BASE_URL = env.NEXT_PUBLIC_BASE_URL;
const BASE_MAGNETO_URL = env.NEXT_PUBLIC_MAGNETO_URI;

export class GovCoError extends Error {
  constructor(name: string, message: string, stack?: any) {
    super(message);
    this.name = name;
    if (stack) {
      this.stack = stack;
    }
  }
}

/**
 * utility function used to merge tailwind classes and allows class validations
 * @param inputs
 * @returns
 */
export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

export const imageLoader = ({ src, width, quality }: ImageLoaderProps) => {
  return `${BASE_URL}/attachment/files/download/${src}?w=${width}&q=${quality || 75}`;
};

export const imageLoaderUrl = ({ src }: ImageLoaderProps) => {
  return `${BASE_URL}/files/${src}`;
};

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const getDownloadURL = (src: string) => {
  return `${BASE_URL}/attachment/files/download/${src}`;
};

export const getDownloadMagnetoURL = (src: string) => {
  return `${BASE_MAGNETO_URL}/attachment/files/download/Inline/${src}`;
};

export function isValidUUID(uuid?: string | null): boolean {
  // Reject non-strings / empty values early
  if (!uuid || typeof uuid !== 'string') return false;
  // Try strict RFC 4122 validation first (versions 1-5, variant 8/9/a/b)
  const rfcUuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (rfcUuidRegex.test(uuid)) return true;

  // Fallback: accept generic GUID/UUID-like strings composed of hex groups
  // This allows non-RFC GUIDs such as your example which has a non-standard
  // version nibble in the 3rd group.
  const genericUuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return genericUuidRegex.test(uuid);
}

/**
 * Alias with a shorter, commonly-used name. Accepts any value and returns boolean.
 */
export const isUuid = (value: unknown): boolean => {
  if (!value || typeof value !== 'string') return false;
  return isValidUUID(value);
};

export const getUploadMagnetoURL = (file: any): [string, any] => {
  const formData = new FormData();
  formData.append('file', file);
  return [`${BASE_MAGNETO_URL}/attachment/files`, formData];
};

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const formatUrlParams = (json: any) => {
  return (
    '?' +
    Object.keys(json)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(json[key])}`)
      .join('&')
  );
};

export const useVerifyPassword = (passValue: string) => {
  if (!passValue || passValue.trim() === '')
    return {
      numChars: false,
      hasUppercase: false,
      hasLowecase: false,
      specialChar: false,
      hasNumber: false,
      noSpace: false,
    };

  return {
    numChars: passValue.length >= 9 && passValue.length <= 16,
    hasUppercase: /[A-Z]/.test(passValue),
    hasLowecase: /[a-z]/.test(passValue),
    specialChar: /(?=.*[@$!%*?&+_])/.test(passValue),
    hasNumber: /[0-9]/.test(passValue),
    noSpace: !/\s/.test(passValue),
  };
};

export function addZeroToNumber(receivedNumber: number) {
  const numberStr = receivedNumber.toString();
  if (numberStr.length === 1) {
    return '0' + numberStr;
  } else {
    return numberStr;
  }
}

export function getActiveTheme(theme: Theme) {
  switch (theme) {
    case Theme.GreenDark:
      return 'green__dark__theme';
    case Theme.Gold:
      return 'gold__theme';
    case Theme.ElfGreen:
      return 'elf__green__theme';
    case Theme.Orange:
      return 'orange__theme';
    case Theme.Shiraz:
      return 'shiraz__theme';
    case Theme.RadicalRed:
      return 'radical__red__theme';
    case Theme.BlueDark:
      return 'blue__dark__theme';
    default:
      return 'marine__theme';
  }
}

export function formatEncodedTextToHTML(text: string, titleStyle: string) {
  let resultado = text.replace(/%0A/g, '<br/>');
  resultado = decodeURI(resultado);

  // Se convierte el patrón =texto= en un span subrayado.
  resultado = resultado.replace(
    /\=([^*]+)\=/g,
    `<span class="underline md:inline block">$1</span>`
  );

  // Se convierte el patrón *texto* en un span con la clase pasada por parámetro.
  resultado = resultado.replace(/\*([^*]+)\*/g, `<span class=${titleStyle}>$1</span>`);

  // Nueva funcionalidad: detectar emails y convertirlos en enlaces mailto.
  resultado = resultado.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    `<a href="mailto:$1">$1</a>`
  );

  return resultado;
}

// --- New: structured parser for encoded text ---
// Inline node types (content that lives inside a line)
export type RichInline =
  | { type: 'text'; content: string }
  | { type: 'underline'; content: string }
  | { type: 'styled'; content: string }
  | { type: 'mailto'; href: string; content: string };

// Top-level node: a line that contains an ordered list of inline nodes
export type RichNode = { type: 'line'; children: RichInline[] };

export function parseEncodedTextToNodes(text: string): RichNode[] {
  if (!text) return [];

  // Replace %0A with a real newline, then decode URI sequences
  let decoded = text.replace(/%0A/g, '\n');
  try {
    decoded = decodeURI(decoded);
  } catch (e) {
    // ignore decode errors and keep raw decoded string
  }

  const lines = decoded.split(/\r\n|\n|\r/);
  const out: RichNode[] = [];

  const inlineRegex = /=([^=]+)=|\*([^*]+)\*|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

  for (const rawLine of lines) {
    const children: RichInline[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = inlineRegex.exec(rawLine)) !== null) {
      if (match.index > lastIndex) {
        children.push({ type: 'text', content: rawLine.slice(lastIndex, match.index) });
      }

      if (match[1]) {
        children.push({ type: 'underline', content: match[1] });
      } else if (match[2]) {
        children.push({ type: 'styled', content: match[2] });
      } else if (match[3]) {
        children.push({ type: 'mailto', href: match[3], content: match[3] });
      }

      lastIndex = inlineRegex.lastIndex;
    }

    if (lastIndex < rawLine.length) {
      children.push({ type: 'text', content: rawLine.slice(lastIndex) });
    }

    out.push({ type: 'line', children });
  }

  return out;
}

export function cx(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isCurrentPathOrChild(arg1: string, arg2: string) {
  // console.group();
  // const isMicroSitePage = arg1?.includes('/admin/home/page-config/micro-site/');
  // const isMicroSitePageChild = arg2?.includes('/admin/home/page-config/micro-site/');

  // if (isMicroSitePage) {
  //   console.log('arg1', arg1);
  //   console.log('arg2', arg2);
  // }

  // console.groupEnd();
  // if (isMicroSitePage) return isMicroSitePage === isMicroSitePageChild;

  if (arg1?.toUpperCase() == arg2?.toUpperCase()) {
    return true;
  }
  const isSome = MAIN_GENERAL_URLS.some((item) => {
    return (
      arg1?.toUpperCase().includes(arg2?.toUpperCase()) &&
      // item.children.some((subitem) => subitem.toUpperCase() == arg1?.toUpperCase())
      item?.children?.some((subitem) => arg1?.toUpperCase()?.includes(subitem.toUpperCase()))
    );
  });

  return isSome;
}

export function getInputTypeByTypename(typename: string) {
  return PageBlockConst[typename as keyof PageBlockConstType] ?? '';
}

export function getInputIdFormContext(mObject: any, way: string): any | undefined {
  // Handle nested paths with bracket notation like 'seoInput.metadata.[0].slug'
  // Convert bracket notation to dot notation and split
  const normalizedPath = way.replace(/\.\[(\d+)\]/g, '.$1');
  const parts = normalizedPath.split('.');
  let currentValue = mObject;

  // console.group(`🔍 getInputIdFormContext Debug: ${way}`);
  // console.log('📝 Original path:', way);
  // console.log('🔄 Normalized path:', normalizedPath);
  // console.log('📊 Parts:', parts);
  // console.log('🎯 Initial object:', mObject);

  for (const part of parts) {
    if (currentValue && typeof currentValue === 'object') {
      // Check if part is a numeric index (for arrays)
      const isNumericIndex = /^\d+$/.test(part);

      if (isNumericIndex) {
        // Handle array access
        const index = parseInt(part, 10);
        // console.log(`🔢 Processing array index: ${index}`);
        if (Array.isArray(currentValue) && index >= 0 && index < currentValue.length) {
          currentValue = currentValue[index];
          // console.log(`✅ Array access [${index}] success:`, currentValue);
        } else {
          // console.log(`❌ Array access failed for index ${index}. Current value:`, currentValue);
          // console.log(
          //   `   Is array: ${Array.isArray(currentValue)}, Length: ${Array.isArray(currentValue) ? currentValue.length : 'N/A'}`
          // );
          // console.groupEnd();
          return undefined;
        }
      } else {
        // Handle object property access
        // console.log(`🔑 Processing property: '${part}'`);
        if (part in currentValue) {
          currentValue = currentValue[part];
          // console.log(`✅ Property access '${part}' success:`, currentValue);
        } else {
          // console.log(`❌ Property '${part}' not found in:`, currentValue);
          // console.log(`   Available properties:`, Object.keys(currentValue));
          // console.groupEnd();
          return undefined;
        }
      }
    } else {
      // console.log(`❌ currentValue is not an object:`, currentValue);
      // console.log(`   Type: ${typeof currentValue}`);
      // console.groupEnd();
      return undefined;
    }
  }

  // console.log('🎉 Final result:', currentValue);
  // console.groupEnd();

  return currentValue;
}

export function orderArrayByLength(arr: any[], key: string) {
  arr.sort(function (a, b) {
    const aKey = a[key];
    const bKey = b[key];

    for (let i = 0; i < Math.min(aKey.length, bKey.length); i++) {
      const numA = parseInt(aKey[i]);
      const numB = parseInt(bKey[i]);

      if (numA < numB) {
        return -1;
      }
      if (numA > numB) {
        return 1;
      }
    }

    // Si los números son iguales hasta este punto, ordenar por longitud
    return aKey.length - bKey.length;
  });
}

export function minLengthInArray(arr: any[]) {
  if (!Array.isArray(arr) || arr.length === 0) {
    return 0;
  }

  let minLength = arr[0].length;

  for (let i = 1; i < arr.length; i++) {
    const currentLength = arr[i].length;
    if (currentLength < minLength) {
      minLength = currentLength;
    }
  }

  return minLength;
}

export const formatVideoUrl = (url: string) => {
  const regex =
    /(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/watch\?t=[0-9]+s&v=)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? `https://www.youtube.com/embed/${match[1]}` : url;
};

export function getMinLengthOfArray(list: any[], key: string) {
  if (!list || list.length < 1) {
    return 0;
  }
  const mList = [...list];
  mList.sort((a, b) => a[key].length - b[key].length);
  return mList[0][key].length;
}

function transformToRGBFormat(input: string) {
  const numerosSeparados = input.replace(/,/g, ' ');
  const numeros = numerosSeparados.split(' ');
  const numerosTransformados = numeros.map((numero) => {
    const numeroTransformado = encodeURIComponent(numero);
    return numeroTransformado;
  });
  const salida = `${numerosTransformados.join('%2C')}`;
  return salida;
}

export function getBannerEncodedURI(primaryColor = '13 104 75', secondaryColor = '9 71 51') {
  const mURI =
    'data:image/svg+xml,%3Csvg%20width%3D%221280%22%20height%3D%22270%22%20viewBox%3D%220%200%201280%20270%22%20fill%3D%22none%22%20primaryColor%3D%22%2312345%22%20secondaryColor%3D%22%2367890%22%20%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cg%20clip-path%3D%22url(%23clip0_2859_33411)%22%3E%0A%3Crect%20width%3D%221280%22%20height%3D%22270%22%20fill%3D%22rgb(13%2C104%2C75)%22%2F%3E%0A%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M1102.23%20-69.8839L1155.16%20-99.4489L1103.09%20-130.5L1102.23%20-69.8839ZM1041.36%20-100.871L1093.42%20-69.8199L1094.28%20-130.436L1041.36%20-100.871ZM1005.06%20-115.177L1003.84%20-28.583L929.46%20-72.9413L1005.06%20-115.177ZM1003.51%20-5.18815L1002.28%2081.4057L927.903%2037.0475L1003.51%20-5.18815ZM277.301%20339L352.301%20382.301L277.301%20425.602V339ZM353.301%20487.301L278.301%20444V530.602L353.301%20487.301ZM84.3008%20326.301L9.30083%20283L9.30083%20369.602L84.3008%20326.301ZM1269.46%20141.192L1195.08%2096.8341L1270.69%2054.5984L1269.46%20141.192ZM265.5%20448.029L198%20487L265.5%20525.971V448.029ZM1093.66%20-64.3161L1018.05%20-22.0805L1019.28%20-108.674L1093.66%20-64.3161ZM343.471%20286.471L275.971%20247.5V325.442L343.471%20286.471ZM248.971%20103.5L316.471%20142.471L248.971%20181.442V103.5ZM935.376%20132.993L1002.32%20172.915L1003.42%2094.981L935.376%20132.993ZM99.3008%20338L174.301%20381.301L99.3008%20424.602V338ZM1105.87%2040.5665L1180.25%2084.9247L1181.48%20-1.66913L1105.87%2040.5665ZM108%20331L175.5%20292.029V369.971L108%20331ZM1102.45%20126.799L1170.5%2088.787L1103.55%2048.8645L1102.45%20126.799ZM649%20466L694%20440.019V491.981L649%20466ZM676%20312.019L631%20338L676%20363.981V312.019ZM581%20308L626%20282.019V333.981L581%20308ZM587.055%20-25.5L632.418%20-50.8414L587.79%20-77.4564L587.055%20-25.5ZM187.301%20286L262.301%20329.301L187.301%20372.602V286ZM1017.14%2091.3155L1091.52%20135.674L1092.75%2049.0799L1017.14%2091.3155ZM12.3008%20389L87.3008%20432.301L12.3008%20475.602L12.3008%20389ZM1193.58%20-9.19765L1267.96%2035.1606L1269.19%20-51.4333L1193.58%20-9.19765ZM8.9707%20190.5L76.4707%20229.471L8.97071%20268.442L8.9707%20190.5ZM1201.54%20193.766L1268.48%20233.688L1269.59%20155.753L1201.54%20193.766ZM202%20283L262%20248.359V317.641L202%20283ZM1013.71%20178.549L1081.76%20140.536L1014.81%20100.614L1013.71%20178.549ZM285%20336L352.5%20297.029V374.971L285%20336ZM926.497%20122.308L994.542%2084.2962L927.6%2044.3738L926.497%20122.308ZM836.627%20351.269L768.583%20389.281L769.686%20311.346L836.627%20351.269ZM352.5%20399.029L285%20438L352.5%20476.971V399.029ZM999.957%20-15.6369L924.352%2026.5988L925.577%20-59.9951L999.957%20-15.6369ZM584.5%20371.349L547%20393L584.5%20414.651V371.349ZM713%20348L750.5%20326.349V369.651L713%20348ZM563.5%20301.349L526%20323L563.5%20344.651V301.349ZM612%20211L649.5%20189.349V232.651L612%20211ZM695.573%2044.7124L733.375%2023.5946L696.185%201.4155L695.573%2044.7124ZM755.348%20109.705L717.545%20130.823L718.158%2087.5258L755.348%20109.705ZM637.545%20140.823L675.348%20119.705L638.158%2097.5258L637.545%20140.823ZM371%20379L438.5%20340.029V417.971L371%20379ZM841.115%2078.0959L909.16%2040.0838L842.218%200.161423L841.115%2078.0959ZM613.279%20209.564L583.037%20226.458L583.527%20191.821L613.279%20209.564ZM365.583%20101.281L433.627%2063.2689L366.686%2023.3465L365.583%20101.281ZM375%20475L435%20440.359V509.641L375%20475ZM838.403%20-12.9515L906.448%20-50.9635L839.506%20-90.8859L838.403%20-12.9515ZM17.9996%20275L85.4996%20236.029V313.971L17.9996%20275ZM1188.1%20187.347L1263.71%20145.112L1189.33%20100.753L1188.1%20187.347ZM84.9707%20341.5V419.442L17.4707%20380.471L84.9707%20341.5ZM1190.45%200.457962L1189.22%2087.0518L1264.83%2044.8162L1190.45%200.457962ZM93.3008%20529.602L18.3008%20486.301L93.3008%20443V529.602ZM1188.96%20-106.574L1187.74%20-19.9804L1263.34%20-62.216L1188.96%20-106.574ZM522.641%20396L582.641%20430.641L522.641%20465.282V396ZM698.271%20-14.5467L757.774%2020.9399L758.755%20-48.3352L698.271%20-14.5467ZM450.641%20349L510.641%20383.641L450.641%20418.282V349ZM769.599%2033.4675L829.102%2068.9541L830.083%20-0.321045L769.599%2033.4675ZM454.641%20440L514.641%20474.641L454.641%20509.282V440ZM766.886%20-57.5804L826.39%20-22.0938L827.37%20-91.3689L766.886%20-57.5804ZM26.9996%20189L86.9996%20154.359L86.9996%20223.641L26.9996%20189ZM1189.95%20268.721L1250.44%20234.932L1190.93%20199.446L1189.95%20268.721ZM1242.56%20385.128L1182.07%20418.916L1183.05%20349.641L1242.56%20385.128ZM1006.07%20462.916L1066.56%20429.128L1007.05%20393.641L1006.07%20462.916ZM362.641%20301L422.641%20335.641L362.641%20370.282V301ZM479.641%20132.641L419.641%2098V167.282L479.641%20132.641ZM916.414%20118.194L856.91%2082.7077L917.394%2048.9192L916.414%20118.194ZM434.471%20429.471L366.971%20390.5V468.442L434.471%20429.471ZM913.701%2027.1469L854.197%20-8.33967L914.681%20-42.1282L913.701%2027.1469ZM163.641%20195.641L103.641%20161V230.282L163.641%20195.641ZM1177.97%20271.442L1110.47%20232.471L1177.97%20193.5V271.442ZM351.5%20205.874L291.5%20240.515L351.5%20275.156V205.874ZM986.691%20179.679L926.207%20213.468L927.187%20144.193L986.691%20179.679ZM539.272%20168.137L589.62%20143.847L539.979%20118.142L539.272%20168.137ZM363.31%20212.5L415.81%20242.811L363.31%20273.122V212.5ZM862.426%20175.625L914.492%20206.676L915.35%20146.06L862.426%20175.625ZM1130%20326L1077.5%20356.311V295.689L1130%20326ZM695.628%20191.129L747.694%20222.18L748.552%20161.564L695.628%20191.129ZM99.9707%20240.5L167.471%20279.471L99.9707%20318.442V240.5ZM1111.26%20142.483L1178.2%20182.405L1179.3%20104.471L1111.26%20142.483ZM584.31%20434.5L636.81%20464.811L584.31%20495.122V434.5ZM644.589%20-49.48L696.655%20-18.4293L697.513%20-79.045L644.589%20-49.48ZM187.971%20198.5L255.471%20237.471L187.971%20276.442V198.5ZM220.471%2075.4711L152.971%2036.5V114.442L220.471%2075.4711ZM1097.97%20228.442L1030.47%20189.471L1097.97%20150.5V228.442ZM925.471%20340.471L992.971%20379.442V301.5L925.471%20340.471ZM101.971%20445.5L169.471%20484.471L101.971%20523.442V445.5ZM1102.36%20-64.494L1176.74%20-20.1358L1177.96%20-106.73L1102.36%20-64.494ZM199%20385L266.5%20346.029V423.971L199%20385ZM1013.56%2082.8668L1089.17%2040.6312L1014.79%20-3.72706L1013.56%2082.8668ZM177.65%20388.544L178.558%20475.142L103.108%20432.63L177.65%20388.544ZM1102.94%20-53.326L1102.62%2033.276L1177.78%20-9.75012L1102.94%20-53.326ZM187.301%20474.602V388L262.301%20431.301L187.301%20474.602ZM1092.97%2033.6841L1094.19%20-52.9098L1018.59%20-10.6742L1092.97%2033.6841ZM188.31%20491.5L240.81%20521.811L188.31%20552.122V491.5ZM12.3104%20491.5L64.8105%20521.811L12.3105%20552.122L12.3104%20491.5ZM1217.34%20-98.3799L1269.4%20-67.3292L1270.26%20-127.945L1217.34%20-98.3799ZM127%20522L179.5%20491.689V552.311L127%20522Z%22%20fill%3D%22rgb(9%2C71%2C51)%22%2F%3E%0A%3C%2Fg%3E%0A%3Cdefs%3E%0A%3CclipPath%20id%3D%22clip0_2859_33411%22%3E%0A%3Crect%20width%3D%221280%22%20height%3D%22270%22%20fill%3D%22white%22%2F%3E%0A%3C%2FclipPath%3E%0A%3C%2Fdefs%3E%0A%3C%2Fsvg%3E%0A'
      .replace('13%2C104%2C75', transformToRGBFormat(primaryColor))
      .replace('9%2C71%2C51', transformToRGBFormat(secondaryColor));
  return mURI;
}

export const getPrimaryColor = () => {
  const element = document.getElementsByTagName('main');
  const primaryColor = getComputedStyle(element[0]).getPropertyValue('--primary');
  return primaryColor;
};

export const getPrimaryDarkColor = () => {
  const element = document.getElementsByTagName('main');
  const primaryDarkColor = getComputedStyle(element[0]).getPropertyValue('--primary-dark');
  return primaryDarkColor;
};

export const isEmptyObj = (obj: any): boolean => {
  if (!obj) return true;
  return Object.keys(obj).length === 0;
};

export function isValidDateString(dateString: string) {
  return !isNaN(Date.parse(dateString));
}

/**
 * Raises an error alert for graph fetchs
 * @param request
 * @returns
 */
type IToastyErrorGraph = { [key: string]: any }; // GraphQLError | { "response": { "errors": GraphQLError } }
export const ToastyErrorGraph = (request: IToastyErrorGraph) => {
  if (request.response && request.response.errors) {
    const error: GraphQLError[] = request.response.errors;
    const message = error.map(({ message }) => `${message}`)[0];
    showErrorToast(message);
    return true;
  }
  if (request.message as GraphQLError) {
    showErrorToast(request.message);
    return true;
  }
  return false;
};

// Permisos base (sin sufijo de tipo de usuario)
export const permissionsKeys = { adminVersion: 'admin-version.page-version.find' } as const;

/** Tipos de usuario que pueden aparecer al final del scope */
const userTypes = ['admin', 'officer'] as const;

/**
 * Devuelve `true` si el usuario posee el permiso, independientemente
 * de si está sufijado con `.admin` o `.officer`.
 * Si el usuario es SuperAdmin, siempre retorna `true`.
 *
 * @param userPermissions - Array con los scopes/permisos del usuario.
 * @param basePermission  - Permiso base
 */
export function hasPermission(userPermissions: readonly string[], basePermission: string): boolean {
  // Verificar si el usuario es SuperAdmin
  const userAuth = localStorage.getItem('userauth');
  if (userAuth) {
    try {
      const userData = JSON.parse(userAuth);
      if (userData?.type === 'SuperAdmin') {
        return true; // SuperAdmin tiene acceso a todo
      }
    } catch (error) {
      // Si hay error al parsear, continuar con la lógica normal
    }
  }

  // Para usuarios que no son SuperAdmin, solo verificar exact match
  // Los permisos deben coincidir exactamente
  return userPermissions.includes(basePermission);
}

export function getUserPermissions() {
  const userAuth = localStorage.getItem('userauth');
  if (!userAuth) return [];
  const userAuthParsed = JSON.parse(userAuth);
  const userPermissions = userAuthParsed?.user?.permissions || userAuthParsed?.permissions || [];

  return userPermissions;
}

export function setLocalStorageItem(key: string, value: string) {
  localStorage.setItem(key, value);

  // Dispará un StorageEvent manual para que la misma pestaña se entere
  window.dispatchEvent(new StorageEvent('storage', { key, newValue: value }));
}

export function removeLocalStorageItem(key: string) {
  localStorage.removeItem(key);
  window.dispatchEvent(new StorageEvent('storage', { key, newValue: null }));
}
export function getInitialsFromWords(input: string): string {
  const connectors = [
    'de',
    'y',
    'a',
    'el',
    'la',
    'los',
    'las',
    'del',
    'al',
    'en',
    'por',
    'con',
    'para',
    'sin',
    'sobre',
    'entre',
  ];

  return input
    .split(' ')
    .filter((word) => !connectors.includes(word.toLowerCase())) // Ignorar conectores
    .map((word) => word?.[0]?.toUpperCase()) // Tomar la primera letra de cada palabra
    .join(''); // Unir las iniciales
}

export const safeParse = (str?: string) => {
  try {
    if (!str) return '';
    const first = JSON.parse(str);
    const obj = typeof first === 'string' ? JSON.parse(first) : first;
    return JSON.stringify(obj, null, 2);
  } catch (e) {
    return str || '';
  }
};

/**
 * Descarga un archivo basado en el objeto FileInfo
 * @param fileInfo - Información del archivo a descargar
 * @param customFileName - Nombre personalizado para el archivo (opcional)
 * @returns Promise<void>
 */
export const downloadFileFromFileInfo = async (
  fileInfo: {
    downloadUrl?: string | null;
    url?: string | null;
    fileName: string;
    fileExtension: string;
  },
  customFileName?: string
): Promise<void> => {
  try {
    // Determinar la URL de descarga
    const fileUrl = fileInfo.downloadUrl || fileInfo.url;

    if (!fileUrl) {
      throw new Error('No se encontró URL del archivo');
    }

    // Determinar el nombre del archivo
    const finalFileName = customFileName || fileInfo.fileName;
    const fileNameWithExtension = finalFileName.includes('.')
      ? finalFileName
      : `${finalFileName}.${fileInfo.fileExtension}`;

    // Intentar descarga directa con fetch y blob (más confiable)
    try {
      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error(`Error al obtener el archivo: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();

      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileNameWithExtension;

      // Agregar al DOM temporalmente para activar la descarga
      document.body.appendChild(link);
      link.click();

      // Limpiar
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (fetchError) {
      console.warn('Descarga con fetch falló, intentando método alternativo:', fetchError);

      // Fallback: crear enlace directo con atributos de descarga
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileNameWithExtension;
      link.setAttribute('download', fileNameWithExtension);
      link.setAttribute('target', '_blank');

      // Para algunos navegadores, es necesario agregar al DOM
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error('Error al descargar el archivo:', error);
    showErrorToast(
      `Error al descargar el archivo: ${error instanceof Error ? error.message : 'Error desconocido'}`
    );
    throw error;
  }
};

/**
 * Descarga un archivo desde una URL
 *
 * @param fileUrl - URL completa del archivo a descargar
 * @param filename - Nombre del archivo con extensión
 * @returns Promise<void>
 *
 * @example
 * ```ts
 * await downloadFile('https://example.com/file.pdf', 'documento.pdf');
 * ```
 */
export const downloadFile = async (fileUrl: string, filename: string): Promise<void> => {
  try {
    if (!fileUrl) {
      throw new Error('URL del archivo no proporcionada');
    }

    if (!filename) {
      throw new Error('Nombre del archivo no proporcionado');
    }

    // Intentar descarga con fetch y blob (más confiable para CORS y archivos grandes)
    try {
      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error(`Error al obtener el archivo: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();

      // Crear enlace de descarga
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = filename;

      // Agregar al DOM temporalmente para activar la descarga
      document.body.appendChild(link);
      link.click();

      // Limpiar
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (fetchError) {
      console.warn('Descarga con fetch falló, intentando método alternativo:', fetchError);

      // Fallback: crear enlace directo (funciona si el servidor permite acceso directo)
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename;
      link.setAttribute('download', filename);
      link.setAttribute('target', '_blank');

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error('Error al descargar el archivo:', error);
    showErrorToast(
      `Error al descargar el archivo: ${error instanceof Error ? error.message : 'Error desconocido'}`
    );
    throw error;
  }
};

/**
 * Obtiene el token del cliente (usuario público) desde las cookies
 * @returns El token del cliente o undefined si no existe
 *
 * @example
 * ```ts
 * const token = getPublicToken();
 * if (token) {
 *   // Usuario público autenticado
 * }
 * ```
 */
export const getPublicToken = (): string | undefined => {
  return Cookies.get(TOKEN_CLIENT);
};

/**
 * Obtiene el token del administrador desde las cookies
 * @returns El token del administrador o undefined si no existe
 *
 * @example
 * ```ts
 * const token = getAdminToken();
 * if (token) {
 *   // Usuario administrador autenticado
 * }
 * ```
 */
export const getAdminToken = (): string | undefined => {
  return Cookies.get(TOKEN_ADMIN);
};
